import {
	baseApiHeaders,
	type CURRENCYCODETYPE,
	FIXEDCACHEKEYS,
	HIDE_TOAST_KEY,
	type IApiResponse,
	type IBank,
	type IBeneficiary,
	type IConvertFiatToUsdReqData,
	type IRequestPaymentReqData,
	type IRewardPoints,
	type ISaveWalletAddress,
	type ISwapCurrencyReqData,
	type IUserPreference,
	type IValidateBankDetails,
	type IWalletAddress,
	type IWalletInfo,
	type IWithdrawalsHistory,
	type IWithdrawUsdReqData,
	type NetworkType,
	type ParamType,
	type TokenType,
	type TwoFaType,
} from 'BreetHelpers';

import { setBeneficiaries, setSelectedBeneficiary } from '../app';
import { apiSlice } from './apiSlice';

export const paymentApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		addBeneficiary: builder.mutation<
			IApiResponse<IUserPreference>,
			{ id: string; accountNumber: string; accountName?: string; narration: string }
		>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/banks/add',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetBeneficiaries'],
		}),
		getBanks: builder.query<IApiResponse<IBank[]>, { currency: Lowercase<CURRENCYCODETYPE> }>({
			query: ({ currency }) => ({
				url: `/payments/banks?currency=${currency}`,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			providesTags: ['GetBanks'],
		}),
		getWalletInfo: builder.query<IApiResponse<IWalletInfo>, void>({
			query: () => ({ url: '/payments/wallet-addresses/assets', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
		}),
		getBeneficiaries: builder.query<IApiResponse<IBeneficiary[]>, void>({
			query: () => ({ url: '/payments/banks/user', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					const beneficiaries = data.data ?? [];
					dispatch(setBeneficiaries(beneficiaries));

					const selectedBeneficiary = beneficiaries.find((beneficiary) => beneficiary.autoSettlement) ?? beneficiaries[0];

					dispatch(setSelectedBeneficiary(selectedBeneficiary));
				} catch (error) {
					console.error(error);
				}
			},
			providesTags: ['GetBeneficiaries'],
		}),
		deleteBeneficiaries: builder.mutation<IApiResponse, string>({
			query: (beneficiaryId) => ({
				method: 'DELETE',
				url: `/payments/banks/bank/${beneficiaryId}`,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetBeneficiaries', 'GetUserPreference'],
		}),
		validateBank: builder.mutation<IApiResponse<IValidateBankDetails>, { id: string; accountNumber: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/banks/validate',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		updateAutoSettlement: builder.mutation<IApiResponse<IUserPreference>, { bankId: string; otp?: TwoFaType }>({
			query: ({ bankId, ...rest }) => ({
				method: 'PUT',
				url: `/payments/banks/bank/${bankId}/settlement`,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
				body: rest,
			}),
			invalidatesTags: ['GetUserPreference', 'GetBeneficiaries'],
		}),
		disableAutoSettlement: builder.mutation<IApiResponse<IUserPreference>, { bankId: string; otp?: TwoFaType }>({
			query: ({ bankId, ...rest }) => ({
				method: 'PATCH',
				url: `/payments/banks/bank/${bankId}/settlement`,
				body: rest,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference', 'GetBeneficiaries'],
		}),
		requestPayment: builder.mutation<IApiResponse, IRequestPaymentReqData>({
			query: ({ bankId, ...body }) => ({
				method: 'POST',
				url: `/payments/withdraw/bank/${bankId}`,
				body,
			}),
			invalidatesTags: ['GetUserWallets', 'AllTransactions'],
		}),
		redeemRewards: builder.mutation<IApiResponse, { pin: string; points: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/rewards/redeem',
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
				body,
			}),
			invalidatesTags: ['UserRewardPoints'],
		}),
		getSavedWalletAddresses: builder.query<IApiResponse<IWalletAddress[]>, void>({
			query: () => ({ url: '/payments/wallet-addresses', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			providesTags: ['GetSavedWalletAddresses'],
		}),
		saveWalletAddress: builder.mutation<
			IApiResponse<ISaveWalletAddress>,
			{ address: string; token: TokenType; network: NetworkType; label: string }
		>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/wallet-addresses',
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
				body,
			}),
			invalidatesTags: ['GetSavedWalletAddresses'],
		}),
		deleteWalletAddress: builder.mutation<IApiResponse, { walletId: string }>({
			query: ({ walletId }) => ({
				method: 'DELETE',
				url: `/payments/wallet-addresses/${walletId}`,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetSavedWalletAddresses'],
		}),
		withdrawUSD: builder.mutation<IApiResponse<ISaveWalletAddress>, IWithdrawUsdReqData>({
			query: ({ walletId, ...rest }) => ({
				method: 'POST',
				url: `/payments/withdraw/address/${walletId}`,
				body: rest,
			}),
			invalidatesTags: ['GetUserWallets', 'AllTransactions', 'GetUserPreference'],
		}),
		convertFiatToUSD: builder.mutation<IApiResponse, IConvertFiatToUsdReqData>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/fiat-to-usd',
				body,
			}),
			invalidatesTags: ['GetUserWallets', 'AllTransactions'],
		}),
		swapCurrency: builder.mutation<IApiResponse, ISwapCurrencyReqData>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/swap-currency',
				body,
			}),
			invalidatesTags: ['GetUserWallets', 'AllTransactions'],
		}),
		sellUSD: builder.mutation<IApiResponse<ISaveWalletAddress>, { amount: number; bank?: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/payments/convert',
				body,
			}),
			invalidatesTags: ['GetUserWallets', 'AllTransactions'],
		}),
		getWithdrawalById: builder.query<IApiResponse<IWithdrawalsHistory>, string>({
			query: (id) => ({ url: `/payments/withdrawal/${id}` }),
		}),
		getWithdrawals: builder.query<IApiResponse<IWithdrawalsHistory[]>, ParamType>({
			query: (params) => ({ url: '/payments/withdrawals', params }),
		}),
		getRewardPoints: builder.query<IApiResponse<IRewardPoints[]>, void>({
			query: () => ({ url: '/payments/rewards/points' }),
			providesTags: ['UserRewardPoints'],
		}),
	}),
});

export const {
	useGetBanksQuery,
	useValidateBankMutation,
	useGetBeneficiariesQuery,
	useUpdateAutoSettlementMutation,
	useAddBeneficiaryMutation,
	useDisableAutoSettlementMutation,
	useRequestPaymentMutation,
	useDeleteBeneficiariesMutation,
	useRedeemRewardsMutation,
	useGetWalletInfoQuery,
	useSaveWalletAddressMutation,
	useGetSavedWalletAddressesQuery,
	useWithdrawUSDMutation,
	useDeleteWalletAddressMutation,
	useSellUSDMutation,
	useGetWithdrawalByIdQuery,
	useGetWithdrawalsQuery,
	useGetRewardPointsQuery,
	useConvertFiatToUSDMutation,
	useSwapCurrencyMutation,
} = paymentApiSlice;

export const selectSavedWalletAddresses = paymentApiSlice.endpoints.getSavedWalletAddresses.select();

export const selectSaveWalletAddresses = paymentApiSlice.endpoints.saveWalletAddress.select({
	fixedCacheKey: FIXEDCACHEKEYS.SAVE_WALLET_ADDRESS,
	requestId: undefined,
});
